import React, { useState, useEffect } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'

interface RangeSliderProps {
  min: number
  max: number
  minValue: number | undefined
  maxValue: number | undefined
  onMinChange: (value: number | undefined) => void
  onMaxChange: (value: number | undefined) => void
  label: string
  formatValue?: (value: number) => string
  className?: string
}

const RangeSlider: React.FC<RangeSliderProps> = ({
  min,
  max,
  minValue,
  maxValue,
  onMinChange,
  onMaxChange,
  label,
  formatValue = (value) => value.toString(),
  className = ''
}) => {
  const [localMin, setLocalMin] = useState<string>(minValue?.toString() || '')
  const [localMax, setLocalMax] = useState<string>(maxValue?.toString() || '')

  useEffect(() => {
    setLocalMin(minValue?.toString() || '')
    setLocalMax(maxValue?.toString() || '')
  }, [minValue, maxValue])

  const handleMinChange = (value: string) => {
    setLocalMin(value)
    const numValue = value ? Number(value) : undefined
    if (numValue !== undefined && (maxValue === undefined || numValue <= maxValue)) {
      onMinChange(numValue)
    }
  }

  const handleMaxChange = (value: string) => {
    setLocalMax(value)
    const numValue = value ? Number(value) : undefined
    if (numValue !== undefined && (minValue === undefined || numValue >= minValue)) {
      onMaxChange(numValue)
    }
  }

  const handleMinBlur = () => {
    const numValue = localMin ? Number(localMin) : undefined
    if (numValue !== undefined && numValue < min) {
      setLocalMin(min.toString())
      onMinChange(min)
    } else if (numValue !== undefined && maxValue !== undefined && numValue > maxValue) {
      setLocalMin(maxValue.toString())
      onMinChange(maxValue)
    }
  }

  const handleMaxBlur = () => {
    const numValue = localMax ? Number(localMax) : undefined
    if (numValue !== undefined && numValue > max) {
      setLocalMax(max.toString())
      onMaxChange(max)
    } else if (numValue !== undefined && minValue !== undefined && numValue < minValue) {
      setLocalMax(minValue.toString())
      onMaxChange(minValue)
    }
  }

  return (
    <Card variant="default" className={className}>
      <CardContent className="p-4">
        <div className="space-y-3">
          <label className="text-sm font-medium">{label}</label>
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <Input
                type="number"
                placeholder={formatValue(min)}
                value={localMin}
                onChange={(e) => handleMinChange(e.target.value)}
                onBlur={handleMinBlur}
                className="text-sm"
              />
            </div>
            <span className="text-muted-foreground">to</span>
            <div className="flex-1">
              <Input
                type="number"
                placeholder={formatValue(max)}
                value={localMax}
                onChange={(e) => handleMaxChange(e.target.value)}
                onBlur={handleMaxBlur}
                className="text-sm"
              />
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            Range: {formatValue(min)} - {formatValue(max)}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default RangeSlider
